// src/admin/pages/MonthlyCAManagement.jsx
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  FileText,
  Upload,
  Eye,
  Edit2,
  Trash2,
  Image as ImageIcon,
  LayoutGrid,
  List,
  BookOpen,
} from "lucide-react";
import { useAdminData } from "../context/AdminDataContext";
import Modal from "../components/Modal";
import PdfPreviewModal from "../components/PdfPreviewModal";

const monthsList = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const yearsList = ["2026", "2025", "2024"];

const MonthlyCAManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    monthlyCA,
    addMonthlyCA,
    updateMonthlyCA,
    deleteMonthlyCA,
    togglePublishMonthlyCA,
  } = useAdminData();

  // Layout view switcher
  const [viewMode, setViewMode] = useState("grid"); // 'grid' | 'table'

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedYear, setSelectedYear] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");

  // Modals
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewPdfTitle, setPreviewPdfTitle] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    title: "",
    month: "October",
    year: "2026",
    description: "",
    status: "Published",
    pages: 48,
    pdfName: "",
    pdfUrl: "",
    coverImage: "",
  });

  // URL trigger for ?action=new
  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openAddModal();
      searchParams.delete("action");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [selectedThumbFile, setSelectedThumbFile] = useState(null);

  const openAddModal = () => {
    setEditingItem(null);
    setSelectedPdfFile(null);
    setSelectedThumbFile(null);
    setFormData({
      title: "",
      month: "October",
      year: "2026",
      description: "",
      status: "Published",
      pages: 48,
      pdfName: "",
      pdfUrl: "",
      coverImage: "",
    });
    setIsFormModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setSelectedPdfFile(null);
    setSelectedThumbFile(null);
    setFormData({
      title: item.title,
      month: item.month,
      year: item.year,
      description: item.description || "",
      status: item.status || "Published",
      pages: item.pages || 48,
      pdfName: item.pdfName || "",
      pdfUrl: item.pdfUrl || "",
      coverImage: item.coverImage || "",
    });
    setIsFormModalOpen(true);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert("Please provide a title for the Monthly Magazine edition.");
      return;
    }

    if (editingItem) {
      updateMonthlyCA(editingItem.id, formData, selectedPdfFile, selectedThumbFile);
    } else {
      addMonthlyCA(formData, selectedPdfFile, selectedThumbFile);
    }
    setIsFormModalOpen(false);
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedPdfFile(file);
      const mockUrl = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        pdfName: file.name,
        pdfUrl: mockUrl,
      }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedThumbFile(file);
      const mockUrl = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        coverImage: mockUrl,
      }));
    }
  };

  // Filter items
  const filteredItems = (monthlyCA || []).filter((item) => {
    const matchesSearch =
      (item?.title || "").toLowerCase().includes((searchQuery || "").toLowerCase()) ||
      (item?.description &&
        item.description.toLowerCase().includes((searchQuery || "").toLowerCase()));

    const matchesYear =
      selectedYear === "All" || String(item.year) === selectedYear;

    const matchesMonth =
      selectedMonth === "All" || item.month === selectedMonth;

    const matchesStatus =
      selectedStatus === "All" || item.status === selectedStatus;

    return matchesSearch && matchesYear && matchesMonth && matchesStatus;
  });

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
        <div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">
            Monthly Current Affairs Magazine
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Manage comprehensive monthly UPSC compilations, dossiers, and revision PDFs.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="hidden sm:flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${
                viewMode === "grid"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-900"
              }`}
              title="Grid View"
            >
              <LayoutGrid size={15} />
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${
                viewMode === "table"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-900"
              }`}
              title="Table View"
            >
              <List size={15} />
            </button>
          </div>

          <button
            onClick={openAddModal}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs shadow-lg shadow-amber-600/20 transition-all transform active:scale-95"
          >
            <Plus size={16} /> Upload Monthly Magazine
          </button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search magazine titles..."
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            />
          </div>

          {/* Year Filter */}
          <div>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            >
              <option value="All">All Years</option>
              {yearsList.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {/* Month Filter */}
          <div>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            >
              <option value="All">All Months</option>
              {monthsList.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            >
              <option value="All">All Statuses</option>
              <option value="Published">Published</option>
              <option value="Draft">Draft</option>
            </select>
          </div>
        </div>

        {/* Count */}
        <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
          <span>
            Showing <strong className="text-slate-800">{filteredItems.length}</strong> of{" "}
            {monthlyCA.length} magazine editions
          </span>
          {(searchQuery || selectedYear !== "All" || selectedMonth !== "All" || selectedStatus !== "All") && (
            <button
              onClick={() => {
                setSearchQuery("");
                setSelectedYear("All");
                setSelectedMonth("All");
                setSelectedStatus("All");
              }}
              className="text-xs text-amber-600 font-bold hover:underline"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* Grid View */}
      {viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => {
            const isPublished = item.status === "Published";
            return (
              <div
                key={item.id}
                className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden group"
              >
                {/* Thumbnail / Header */}
                <div className="relative h-44 bg-gradient-to-br from-slate-900 to-[#1e1b4b] overflow-hidden flex items-center justify-center">
                  {item.coverImage ? (
                    <img
                      src={item.coverImage}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="text-center p-6 text-white">
                      <BookOpen size={40} className="mx-auto mb-2 text-amber-400" />
                      <span className="text-xs font-mono tracking-wider uppercase text-slate-300">
                        {item.month} {item.year} Edition
                      </span>
                    </div>
                  )}

                  {/* Badges */}
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-black/60 backdrop-blur-md text-amber-400 border border-amber-400/30">
                      {item.month} {item.year}
                    </span>
                  </div>

                  <div className="absolute top-3 right-3">
                    <button
                      onClick={() => togglePublishMonthlyCA(item.id)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-bold shadow-md transition-all ${
                        isPublished
                          ? "bg-emerald-500 text-white hover:bg-emerald-600"
                          : "bg-amber-500 text-white hover:bg-amber-600"
                      }`}
                    >
                      {item.status}
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                  <div>
                    <h3 className="font-bold text-sm text-slate-900 group-hover:text-amber-600 transition-colors line-clamp-1">
                      {item.title}
                    </h3>
                    <p className="text-xs text-slate-500 line-clamp-2 mt-1 leading-relaxed">
                      {item.description || "Monthly UPSC current affairs compilation."}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                    <span className="font-medium">{item.pages || 48} Pages</span>
                    <span className="font-medium text-slate-400 font-mono">
                      {item.downloads || 450}+ Downloads
                    </span>
                  </div>

                  {/* Actions Bar */}
                  <div className="pt-2 flex items-center justify-between gap-2">
                    <button
                      onClick={() => {
                        setPreviewPdfUrl(item.pdfUrl);
                        setPreviewPdfTitle(item.title);
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-900 text-xs font-bold transition-colors"
                    >
                      <Eye size={14} /> PDF Preview
                    </button>

                    <button
                      onClick={() => openEditModal(item)}
                      className="p-2 rounded-xl text-blue-600 hover:bg-blue-50 transition-colors"
                      title="Edit Edition"
                    >
                      <Edit2 size={16} />
                    </button>

                    <button
                      onClick={() => setDeleteConfirmId(item.id)}
                      className="p-2 rounded-xl text-rose-600 hover:bg-rose-50 transition-colors"
                      title="Delete Edition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                  <th className="py-3.5 px-4">Edition / Title</th>
                  <th className="py-3.5 px-3">Month & Year</th>
                  <th className="py-3.5 px-3 text-center">Pages</th>
                  <th className="py-3.5 px-3 text-center">PDF</th>
                  <th className="py-3.5 px-3 text-center">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredItems.map((item) => {
                  const isPublished = item.status === "Published";
                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50/70 transition-colors group"
                    >
                      <td className="py-4 px-4 max-w-sm">
                        <div className="font-bold text-slate-900 group-hover:text-amber-600 transition-colors line-clamp-1">
                          {item.title}
                        </div>
                        <div className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">
                          {item.description}
                        </div>
                      </td>
                      <td className="py-4 px-3 whitespace-nowrap font-semibold text-slate-700">
                        {item.month} {item.year}
                      </td>
                      <td className="py-4 px-3 text-center font-mono text-slate-600">
                        {item.pages || 48}
                      </td>
                      <td className="py-4 px-3 text-center whitespace-nowrap">
                        <button
                          onClick={() => {
                            setPreviewPdfUrl(item.pdfUrl);
                            setPreviewPdfTitle(item.title);
                          }}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-900 text-[11px] font-semibold"
                        >
                          <FileText size={13} /> Preview
                        </button>
                      </td>
                      <td className="py-4 px-3 text-center whitespace-nowrap">
                        <button
                          onClick={() => togglePublishMonthlyCA(item.id)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${
                            isPublished
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-amber-50 text-amber-700 border border-amber-200"
                          }`}
                        >
                          {item.status}
                        </button>
                      </td>
                      <td className="py-4 px-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => openEditModal(item)}
                            className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"
                            title="Edit"
                          >
                            <Edit2 size={15} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(item.id)}
                            className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50"
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Monthly Modal */}
      {isFormModalOpen && (
        <Modal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          title={editingItem ? "Edit Monthly Magazine" : "Upload Monthly Current Affairs"}
          subtitle="Compile monthly GS issues into a downloadable PDF publication"
          maxWidth="max-w-2xl"
        >
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Edition Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="e.g. October 2026 - UPSC Current Affairs Comprehensive Compendium"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Month *
                </label>
                <select
                  value={formData.month}
                  onChange={(e) =>
                    setFormData({ ...formData, month: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                >
                  {monthsList.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Year *
                </label>
                <select
                  value={formData.year}
                  onChange={(e) =>
                    setFormData({ ...formData, year: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                >
                  {yearsList.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                >
                  <option value="Published">Published</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Description / Highlights
              </label>
              <textarea
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Key themes covered: SCO Summit, National Quantum Mission, Green Hydrogen Corridors..."
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              />
            </div>

            {/* Upload Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="p-3.5 border border-dashed border-slate-300 rounded-xl bg-slate-50/50">
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Upload PDF Compendium
                </label>
                <div className="flex items-center gap-2 mt-2">
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-sm">
                    <Upload size={14} /> Browse PDF
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handlePdfUpload}
                      className="hidden"
                    />
                  </label>
                  {formData.pdfName ? (
                    <span className="text-[11px] font-mono text-emerald-700 truncate max-w-[150px]">
                      {formData.pdfName}
                    </span>
                  ) : (
                    <span className="text-[11px] text-slate-400">
                      Sample PDF will be linked
                    </span>
                  )}
                </div>
              </div>

              <div className="p-3.5 border border-dashed border-slate-300 rounded-xl bg-slate-50/50">
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Thumbnail Cover Art
                </label>
                <div className="flex items-center gap-2 mt-2">
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-sm">
                    <ImageIcon size={14} /> Choose Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                  {formData.coverImage && (
                    <img
                      src={formData.coverImage}
                      alt="Cover"
                      className="w-8 h-8 rounded object-cover border border-slate-200"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsFormModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 text-xs font-bold text-white bg-amber-600 hover:bg-amber-500 rounded-xl shadow-md transition-all"
              >
                {editingItem ? "Save Changes" : "Publish Monthly Edition"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <Modal
          isOpen={!!deleteConfirmId}
          onClose={() => setDeleteConfirmId(null)}
          title="Delete Monthly Magazine Edition"
          maxWidth="max-w-md"
          footer={
            <div className="w-full flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteMonthlyCA(deleteConfirmId);
                  setDeleteConfirmId(null);
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-rose-700 hover:bg-rose-800 rounded-xl shadow"
              >
                Delete Edition
              </button>
            </div>
          }
        >
          <p className="text-xs text-slate-600 leading-relaxed">
            Are you sure you want to remove this monthly current affairs edition? This will unpublish the download link for students.
          </p>
        </Modal>
      )}

      {/* PDF Document Preview Modal */}
      {previewPdfUrl && (
        <PdfPreviewModal
          isOpen={!!previewPdfUrl}
          onClose={() => setPreviewPdfUrl(null)}
          pdfUrl={previewPdfUrl}
          title={previewPdfTitle}
        />
      )}
    </div>
  );
};

export default MonthlyCAManagement;
